/**
 * Popup quotidien : alerte le vendeur sur ses feuilles de clarification manquantes.
 * Message court et direct, affiché une seule fois par jour.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, FileWarning } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_PREFIX = 'clarifAlertSeen';

export default function ClarificationAlert() {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!token || !user) return;
    // Les superviseurs ne vendent pas eux-mêmes
    if (['admin', 'partner', 'responsable_agence'].includes(user.role)) return;

    const todayKey = `${STORAGE_PREFIX}_${user.email}_${new Date().toISOString().slice(0, 10)}`;
    if (localStorage.getItem(todayKey)) return;

    axios.get(`${API_URL}/ilek-clarif/my-coverage`)
      .then(r => {
        if (r.data?.should_show_popup) {
          setData(r.data);
          setOpen(true);
          localStorage.setItem(todayKey, '1');
        }
      })
      .catch(() => {});
  }, [user, token]);

  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md border-orange-300 dark:border-orange-800" data-testid="clarif-alert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-orange-600 dark:text-orange-400">
            <AlertTriangle className="h-6 w-6" />
            Feuilles de clarification
          </DialogTitle>
        </DialogHeader>

        <div className="py-2 space-y-4">
          <div className="rounded-xl bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900 p-5 text-center">
            <div className="text-5xl font-bold text-orange-600 dark:text-orange-400" data-testid="clarif-alert-rate">
              {data.missing_rate}%
            </div>
            <p className="mt-2 text-base font-semibold text-orange-800 dark:text-orange-300">
              ATTENTION il manque {data.missing_rate}% de vos feuilles de clarification.
            </p>
            <p className="text-base font-semibold text-orange-800 dark:text-orange-300">
              Merci de les télécharger
            </p>
          </div>

          <div className="flex items-center justify-center gap-6 text-sm">
            <div className="text-center">
              <div className="font-bold text-lg" data-testid="clarif-alert-missing">{data.missing}</div>
              <div className="text-muted-foreground text-xs">manquantes</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-lg">{data.received}</div>
              <div className="text-muted-foreground text-xs">transmises</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-lg">{data.total}</div>
              <div className="text-muted-foreground text-xs">ventes validées</div>
            </div>
          </div>

          {data.missing_items?.length > 0 && (
            <div className="max-h-40 overflow-y-auto rounded-lg border divide-y text-xs">
              {data.missing_items.map(i => (
                <div key={`${i.partner}-${i.ref}`} className="flex items-center justify-between px-3 py-2">
                  <span className="flex items-center gap-2 min-w-0">
                    <FileWarning className="size-3.5 shrink-0 text-orange-500" />
                    <span className="font-medium truncate">{i.client}</span>
                  </span>
                  <span className="text-muted-foreground shrink-0 ml-2">{i.partner} · {i.ref} · {i.date}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button
          onClick={() => setOpen(false)}
          className="w-full bg-orange-600 hover:bg-orange-700 text-white"
          data-testid="clarif-alert-close"
        >
          J'ai compris, je les télécharge
        </Button>
      </DialogContent>
    </Dialog>
  );
}
